// src/pages/ChangePasswordPage.js
import React, { useState, useEffect } from "react";
import Header from "../components/Header";
import { changePassword, getUserProfile } from "../services/api";
import styles from "../css/shared.module.css";
import { FiEye, FiEyeOff, FiSave, FiUser, FiMail, FiCalendar, FiClock, FiShield } from "react-icons/fi";
import Footer from "../components/Footer";

const ChangePasswordPage = () => {
  const [form, setForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [visibility, setVisibility] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // ✅ Estados separados para mensajes de éxito y error
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // ✅ Estados para información del usuario
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Cargar información del usuario
  useEffect(() => {
    const loadUserProfile = async () => {
      try {
        setProfileLoading(true);
        const response = await getUserProfile();
        if (response.success) {
          setUserProfile(response.data);
        } else {
          setErrorMessage("Error al cargar la información del usuario");
        }
      } catch (error) {
        console.error("Error loading user profile:", error);
        setErrorMessage("Error al cargar la información del usuario");
      } finally {
        setProfileLoading(false);
      }
    };

    loadUserProfile();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleVisibility = (field) => {
    setVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");
    setIsLoading(true);

    if (form.newPassword !== form.confirmPassword) {
      setErrorMessage("Las nuevas contraseñas no coinciden.");
      setIsLoading(false);
      return;
    }

    try {
      const res = await changePassword(form);
      if (res.success) {
        setSuccessMessage("¡Contraseña actualizada con éxito!");
        // Limpiar el formulario en caso de éxito
        setForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        setErrorMessage(res.message || "No se pudo actualizar la contraseña.");
      }
    } catch (err) {
      console.error("Change password error:", err);
      setErrorMessage("Ocurrió un error inesperado. Inténtalo de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  // Helper para renderizar cada campo de contraseña
  const renderPasswordField = (name, placeholder, fieldKey) => (
    <div className={styles.formGroup} key={name}>
      <label htmlFor={name}>{placeholder}</label>
      <div className={styles.passwordWrapper}>
        <input
          type={visibility[fieldKey] ? "text" : "password"}
          id={name}
          name={name}
          placeholder="••••••••"
          className={styles.input}
          value={form[name]}
          onChange={handleChange}
          required
        />
        {/* ✅ Botón accesible para el toggle, no un span */}
        <button
          type="button"
          className={styles.eyeIcon}
          onClick={() => toggleVisibility(fieldKey)}
          aria-label={`Mostrar u ocultar ${placeholder}`}
        >
          {visibility[fieldKey] ? <FiEyeOff /> : <FiEye />}
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.appContainer}>
      {" "}
      {/* Contenedor raíz implícito */}
      <Header />
      <div className={styles.adminPanel}>
        <h2 className={styles.title}>👤 Perfil de Usuario</h2>

        {/* Panel informativo del usuario */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Información de la Cuenta</h3>
          {profileLoading ? (
            <div className={styles.loadingMessage}>
              <p>Cargando información del usuario...</p>
            </div>
          ) : userProfile ? (
            <div className={styles.userInfoGrid}>
              <div className={styles.userInfoItem}>
                <div className={styles.userInfoIcon}>
                  <FiUser />
                </div>
                <div className={styles.userInfoContent}>
                  <label>Nombre de Usuario</label>
                  <span>{userProfile.username}</span>
                </div>
              </div>
              
              <div className={styles.userInfoItem}>
                <div className={styles.userInfoIcon}>
                  <FiMail />
                </div>
                <div className={styles.userInfoContent}>
                  <label>Correo Electrónico</label>
                  <span>{userProfile.email}</span>
                </div>
              </div>
              
              <div className={styles.userInfoItem}>
                <div className={styles.userInfoIcon}>
                  <FiCalendar />
                </div>
                <div className={styles.userInfoContent}>
                  <label>Fecha de Registro</label>
                  <span>{new Date(userProfile.joinedDate).toLocaleDateString('es-ES', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}</span>
                </div>
              </div>
              
              <div className={styles.userInfoItem}>
                <div className={styles.userInfoIcon}>
                  <FiClock />
                </div>
                <div className={styles.userInfoContent}>
                  <label>Fecha de Expiración de Acceso</label>
                  <span className={userProfile.isAccessExpired ? styles.expiredText : styles.validText}>
                    {new Date(userProfile.accessExpirationDate).toLocaleDateString('es-ES', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                    {userProfile.daysUntilExpiration > 0 && (
                      <span className={styles.daysRemaining}>
                        ({userProfile.daysUntilExpiration} días restantes)
                      </span>
                    )}
                  </span>
                </div>
              </div>
              
              <div className={styles.userInfoItem}>
                <div className={styles.userInfoIcon}>
                  <FiShield />
                </div>
                <div className={styles.userInfoContent}>
                  <label>Rol</label>
                  <span className={styles.roleBadge}>
                    {userProfile.role === 'admin' ? 'Administrador' : 'Usuario'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.errorMessage}>
              <p>No se pudo cargar la información del usuario</p>
            </div>
          )}
        </div>

        <h2 className={styles.title}>🔐 Cambiar Contraseña</h2>

        <div className={styles.section}>
          {/* ✅ Mensajes de feedback visualmente destacados */}
          {successMessage && (
            <p className={`${styles.feedbackMessage} ${styles.successMessage}`}>
              {successMessage}
            </p>
          )}
          {errorMessage && (
            <p className={`${styles.feedbackMessage} ${styles.errorMessage}`}>
              {errorMessage}
            </p>
          )}

          <form onSubmit={handleSubmit}>
            {renderPasswordField(
              "currentPassword",
              "Contraseña Actual",
              "current"
            )}
            {renderPasswordField("newPassword", "Nueva Contraseña", "new")}
            {renderPasswordField(
              "confirmPassword",
              "Confirmar Nueva Contraseña",
              "confirm"
            )}

            {/* ✅ Botón con estado de carga y feedback visual */}
            <button
              type="submit"
              className={styles.button}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className={styles.spinner}></div>
              ) : (
                <>
                  <FiSave />
                  <span>Guardar Cambios</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ChangePasswordPage;
